import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { strings } from '../strings';
import { describeError, ToastContext, type ToastApi, type ToastMessage, type ToastTone } from './toast';

const ICONS: Record<ToastTone, typeof Info> = {
  error: CircleAlert,
  warning: TriangleAlert,
  success: CircleCheck,
  info: Info,
};

const TONES: Record<ToastTone, string> = {
  error: 'tone-danger',
  warning: 'tone-warning',
  success: 'tone-success',
  info: 'tone-info',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => setToasts((list) => list.filter((t) => t.id !== id)), []);

  const show = useCallback<ToastApi['show']>(
    (toast) => {
      const id = nextId.current++;
      setToasts((list) => [...list.slice(-4), { ...toast, id }]);
      setTimeout(() => dismiss(id), toast.tone === 'error' ? 10_000 : 5_000);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({ show, error: (error) => show({ tone: 'error', ...describeError(error) }) }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed right-4 bottom-4 z-50 flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-2">
        {toasts.map((toast) => {
          const Icon = ICONS[toast.tone];
          return (
            <div
              key={toast.id}
              role={toast.tone === 'error' ? 'alert' : 'status'}
              className={`pointer-events-auto flex items-start gap-2 rounded-lg border p-3 shadow-lg ${TONES[toast.tone]}`}
            >
              <Icon aria-hidden className="mt-0.5 size-4 shrink-0" />
              <div className="min-w-0 flex-1 text-sm">
                <p className="font-semibold">{toast.title}</p>
                {toast.detail && <p className="mt-0.5 break-words">{toast.detail}</p>}
                {toast.action && (
                  <button
                    type="button"
                    className="btn btn-sm mt-1.5"
                    onClick={() => {
                      dismiss(toast.id);
                      toast.action!.onSelect();
                    }}
                  >
                    {toast.action.label}
                  </button>
                )}
              </div>
              <button
                type="button"
                className="rounded p-0.5 hover:bg-black/10"
                aria-label={strings.common.close}
                onClick={() => dismiss(toast.id)}
              >
                <X aria-hidden className="size-4" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
